import * as Leaflet from 'leaflet';
import { SLDStyler, StyleOptions } from './leaflet-sld';
import { buildPopupContent, attachPopupHoverHandlers } from '../mapview-popup';
import { DOMAIN_COLORS, DOMAIN_MARKERS, POPUP_DEBUG_VIEW } from '../mapview-constants';
import moment from 'moment';
import { EnvironmentalDataSource } from '../../../data/services/types/EnvironmentalDataSource';

/**
 * Dispose of any attached __popupDisposer functions on a Leaflet layer/tree.
 *
 * Walks a Leaflet layer/tree and calls any attached __popupDisposer functions,
 * then remove the property. Accepts an optional layerId for better debug
 * messages.
 */
export function callDisposersOnLayer(obj: any, layerId?: string) {
  try {
    if (!obj) return;
    if (typeof obj.__popupDisposer === 'function') {
      try {
        (obj.__popupDisposer as Function)();
      } catch (e) {
        console.debug('Calling __popupDisposer failed for a layer', layerId, e);
      }
      try {
        delete obj.__popupDisposer;
      } catch (e) {}
    }
    if (obj.eachLayer && typeof obj.eachLayer === 'function') {
      try {
        obj.eachLayer((child: any) => callDisposersOnLayer(child, layerId));
      } catch (e) {
        console.debug('eachLayer traversal failed while cleaning disposers', layerId, e);
      }
    }
  } catch (e) {
    console.debug('callDisposersOnLayer internal error', layerId, e);
  }
}

/**
 * Reducer function for aggregating environmental API
 * response data points into grouped entries keyed by orderField.
 */
function environmentalDrawerDataReducer(environmentalDataSourcesById: any, analytesById: any) {
  return (accumulator: any, dataPoint: any) => ({
    ...accumulator,
    [dataPoint.orderField]: {
      ...dataPoint,
      ...(accumulator[dataPoint.orderField] || {}),
      analyte_id: undefined,
      analyte: undefined,
      environmentalDataSource: environmentalDataSourcesById[dataPoint.environmental_data_source_id],
      data: [
        ...(accumulator[dataPoint.orderField]?.data || []),
        {
          avg: dataPoint.avg,
          min: dataPoint.min,
          max: dataPoint.max,
          sum: dataPoint.sum,
          stddev: dataPoint.stddev,
          stddev_pop: dataPoint.stddev_pop,
          stddev_samp: dataPoint.stddev_samp,
          variance: dataPoint.variance,
          var_pop: dataPoint.var_pop,
          var_samp: dataPoint.var_samp,
          percentile_25: dataPoint.percentile_25,
          percentile_50: dataPoint.percentile_50,
          percentile_75: dataPoint.percentile_75,
          percentile_90: dataPoint.percentile_90,
          percentile_95: dataPoint.percentile_95,
          percentile_99: dataPoint.percentile_99,
          mode: dataPoint.mode,
          count: dataPoint.count,
          observation_entity_id: dataPoint.observation_entity_id,
          entity: dataPoint.entity,
          analyte: analytesById[dataPoint.analyte_id],
        },
      ],
    },
  });
}

/**
 * Detect main geometry type for a GeoJSON-like payload.
 */
export function detectGeometryType(layerData: any): 'polygon' | 'line' | 'point' {
  try {
    const features: any[] = (layerData?.data && layerData?.data.features) || [];
    const types = new Set(features.map((f: any) => f.geometry?.type));
    const typesArr = Array.from(types);
    if (typesArr.some((t) => /Polygon/i.test(t))) return 'polygon';
    if (typesArr.some((t) => /LineString/i.test(t))) return 'line';
    return 'point';
  } catch (e) {
    console.warn('detectGeometryType failed, defaulting to polygon', e);
    return 'polygon';
  }
}

/**
 * Try to recreate a cached API layer (if it contains __apiLayerData metadata) or
 * re-add an existing cached layer to the provided layerGroup/map.
 *
 * Note: If both loadApiLayerUnified and loadApiLayer/loadApiLayerWithSLD are provided,
 * loadApiLayerUnified will be used.
 *
 * Note: If the existing layer has __apiLayerData metadata, it will be recreated with the same data.
 *
 * @param layerId ID of the layer (for logging)
 * @param existing The existing Leaflet layer to recreate or re-add
 * @param ctx Context object containing:
 *   - apiLayers: the map of cached apiLayers by id
 *   - layerGroup: the Leaflet layerGroup to add to
 *   - map: the Leaflet map to add to (if layerGroup is not present)
 *   - loadApiLayerUnified: function to call to load a layer with optional SLD
 *   - loadApiLayer: function to call to load a layer without SLD
 *   - loadApiLayerWithSLD: function to call to load a layer with SLD
 *
 * @returns true if the layer was successfully recreated or re-added, false otherwise
 */
export function recreateOrAddCachedApiLayer(
  layerId: string,
  existing: any,
  ctx: {
    apiLayers: { [key: string]: any };
    layerGroup: any;
    map: any;
    loadApiLayerUnified?: (id: string, data: any, sld?: any) => void;
    loadApiLayer?: (id: string, data: any) => void;
    loadApiLayerWithSLD?: (id: string, data: any, sld?: any) => void;
  }
): boolean {
  if (!existing) return false;
  const meta: any = existing && (existing as any).__apiLayerData ? (existing as any) : null;

  try {
    if (meta && meta.__apiLayerData) {
      try {
        try {
          callDisposersOnLayer(existing);
        } catch (ee) {
          console.debug('Failed to call disposers on existing cached layer', layerId, ee);
        }
        if (ctx.layerGroup) {
          ctx.layerGroup.removeLayer(existing);
        }
      } catch (e) {
        console.debug('Failed to remove existing cached layer before recreate', layerId, e);
      }

      try {
        delete ctx.apiLayers[layerId];
      } catch (e) {
        console.log('Failed to delete cached apiLayers entry', layerId, e);
      }

      const apiData = meta.__apiLayerData;
      const sldProvided = Boolean(meta.__sldProvided);
      const sldContent = meta.__sldContent || apiData?.sld || apiData?.sldUrl;
      try {
        if (sldProvided && sldContent) {
          if (ctx.loadApiLayerUnified) {
            ctx.loadApiLayerUnified(layerId, apiData, sldContent as any);
          } else if (ctx.loadApiLayerWithSLD) {
            ctx.loadApiLayerWithSLD(layerId, apiData, sldContent as any);
          } else if (ctx.loadApiLayer) {
            ctx.loadApiLayer(layerId, apiData);
          }
        } else {
          if (ctx.loadApiLayerUnified) {
            ctx.loadApiLayerUnified(layerId, apiData);
          } else if (ctx.loadApiLayer) {
            ctx.loadApiLayer(layerId, apiData);
          } else if (ctx.loadApiLayerWithSLD) {
            ctx.loadApiLayerWithSLD(layerId, apiData, undefined);
          }
        }
        return true;
      } catch (e) {
        console.warn('Failed to recreate cached api layer', layerId, e);
        return false;
      }
    }

    try {
      try {
        callDisposersOnLayer(existing);
      } catch (ee) {
        console.debug('Failed to call disposers on non-meta existing layer', layerId, ee);
      }
      if (ctx.layerGroup) {
        try {
          if (!ctx.layerGroup.hasLayer(existing)) {
            if (ctx.map) {
              ctx.layerGroup.addLayer(existing).addTo(ctx.map);
            } else {
              ctx.layerGroup.addLayer(existing);
            }
          }
        } catch (addErr) {
          console.warn('Failed to re-add existing cached layer', layerId, addErr);
          return false;
        }
      }
      return true;
    } catch (e) {
      console.warn('Failed to re-add existing cached layer', layerId, e);
      return false;
    }
  } catch (e) {
    console.warn('Error handling cached api layer', layerId, e);
    return false;
  }
}

/**
 * Build Leaflet GeoJSON options for a given API layer.
 *
 * Keeps popup content, styles and SLD fallback encapsulated so both SLD and non-SLD
 * loaders can use the same logic.
 *
 * Stores a disposer on each layer for cleanup.
 */
export function buildGeoJsonOptions(layerId: string, layerData: any, paneName: string, sldStyler?: SLDStyler): any {
  const geoJsonOptions: any = {
    onEachFeature: (feature: any, layer: any) => {
      const nameAttribute = layerData.name_attribute || 'name';
      const displayName =
        (nameAttribute && feature.properties?.[nameAttribute]) ||
        feature.properties?.name ||
        layerData.layer_name ||
        layerData.name ||
        'Unnamed Feature';

      const popupContent = buildPopupContent('feature', feature, layerData, paneName);

      const popupOptions = {
        closeButton: false,
        offset: [5, -5] as [number, number],
        className: 'feature-popup',
        autoPan: true,
        sticky: true,
        minWidth: 220,
      };

      let originalStyle: StyleOptions = {
        weight: 2,
        color: DOMAIN_COLORS[layerData.domain],
        dashArray: '2, 2',
        opacity: 0.5,
        fillOpacity: 0.2,
      };
      if (sldStyler) {
        try {
          originalStyle = sldStyler.getStyleFunction()(feature);
        } catch (e) {
          console.warn('Failed to get style from SLD styler', layerId, e);
        }
      }

      const highlightStyle = {
        weight: 2,
        color: '#fff',
        dashArray: '',
        opacity: 0.8,
        fillOpacity: 0.5,
      };

      try {
        // switch from bindPopup to bindTooltip to fix the issue where
        // popups would open offscreen and would flicker on hover
        layer.bindPopup(popupContent, popupOptions);
      } catch (e) {
        console.warn('bindPopup failed for api feature', layerId, e);
      }

      let popupLocation: Leaflet.LatLng | null = null;

      try {
        // needs to be a popup to support clicking on links
        // you can still get the odd hover flash
        layer.bindPopup(popupContent, popupOptions);
      } catch (e) {
        console.warn('bindPopup failed for api feature', layerId, e);
      }

      // these handlers help us manage popup position on hover we want the popup to appear
      // near the mouse cursor but Leaflet popups default to the center of the feature
      // which for large polygons is not ideal so we capture the mouseover position and then
      // when the popup opens we move it to that position.
      // The events seem to fire in the right order.

      // A number of more manual popup management approaches were tried but they all had
      // issues with either links not being clickable or the popup flickering on large
      // polygons. This approach seems to work best overall.
      layer.on('mouseover', (event: Leaflet.LeafletMouseEvent) => {
        // capture the postition
        popupLocation = event.latlng;

        if (POPUP_DEBUG_VIEW) {
          console.log('mouseover captured popup location', layer.options.pane, layer.getPane().style.zIndex);
        }

        // if the popup is already open we need to close it so it re-opens in the right place
        const p = layer.getPopup();
        if (p.isOpen()) {
          p.close();
        }
      });

      layer.on('mouseout', (event: Leaflet.LeafletMouseEvent) => {
        popupLocation = null;
      });

      layer.on('popupopen', (event: Leaflet.LeafletMouseEvent) => {
        // when the popup opens, if we have a captured location, set it otherwise the popup
        // will be in the center of the feature which for large polygons is not ideal
        // as it will be often off the screen when zoomed in.
        if (popupLocation) {
          event.popup.setLatLng(popupLocation);
        }
      });

      try {
        const disposer = attachPopupHoverHandlers(layer, {
          getOriginalStyle: () => originalStyle,
          getHighlightStyle: () => highlightStyle,
          onClick: () => {},
          bringToFront: true,
        });
        try {
          (layer as any).__popupDisposer = disposer;
        } catch (ee) {
          console.debug('Failed to attach disposer to layer', layerId, ee);
        }
      } catch (e) {
        console.warn('attachPopupHoverHandlers failed for api feature', layerId, e);
      }

      layer.myTag = `apiLayer-${layerId}-GeoJSON`;
    },
    pane: paneName,
    pointToLayer: function (feature: any, latlng: any) {
      if (sldStyler) {
        try {
          return sldStyler.getPointToLayerFunction()(feature, latlng);
        } catch (e) {
          console.warn('sldStyler.getPointToLayerFunction failed', layerId, e);
        }
      }
      return Leaflet.marker(latlng, {
        icon: Leaflet.icon({ iconUrl: `/assets/icons/${DOMAIN_MARKERS[layerData.domain]}` }),
      });
    },
    style: (feature: any) => {
      if (sldStyler) {
        try {
          return sldStyler.getStyleFunction()(feature);
        } catch (e) {
          console.warn('sldStyler.getStyleFunction failed', layerId, e);
        }
      }
      return {
        weight: 2,
        color: DOMAIN_COLORS[layerData.domain],
        dashArray: '2, 2',
        opacity: 0.5,
        fillOpacity: 0.2,
      };
    },
  };

  return geoJsonOptions;
}

/**
 * Build a drawer payload from the environmental API response.
 * Returns an object suitable for passing to DrawerService.setDrawerData.
 */
export function buildEnvironmentalDrawerPayload(response: any): any {
  const environmentalDataSourcesById = (response.environmentalDataSources || []).reduce(
    (accumulator: any, source: any) => ({
      ...accumulator,
      [source.id]: source,
    }),
    {}
  );
  const analytesById = (response.analytes || []).reduce(
    (accumulator: any, analyte: any) => ({
      ...accumulator,
      [analyte.id]: analyte,
    }),
    {}
  );

  const precisionMap: any = {
    raw: 'YYYY-MM-DD HH:mm',
    hour: 'YYYY-MM-DD HH:mm',
    day: 'YYYY-MM-DD',
    week: 'YYYY-MM-DD',
    month: 'YYYY-MM',
  };
  const displayPrecision: any = {
    raw: 'dd/MM/yyyy hh:mma',
    hour: 'dd/MM/yyyy hh:mma',
    day: 'dd/MM/yyyy',
    week: 'dd/MM/yyyy',
    month: 'dd/MM/yyyy',
  };
  const endDateMap: any = {
    raw: { hours: 0 },
    hour: { hours: 0 },
    day: { days: 0 },
    week: { days: 6 },
    month: { months: 1, days: -1 },
  };

  const precision = precisionMap[response.granularity as keyof typeof precisionMap];

  const drawerData = Object.values(
    (response.data || [])
      .map((dataPoint: any) => ({
        ...dataPoint,
        displayDate: moment(dataPoint.date_time).format(precision),
        startDate: moment(dataPoint.date_time).format('YYYY-MM-DD'),
        endDate: moment(dataPoint.date_time)
          .add(endDateMap[response.granularity as keyof typeof endDateMap])
          .format('YYYY-MM-DD'),
        entity: dataPoint.observation_entity_id
          ? response.entities.find((e: any) => e.id === dataPoint.observation_entity_id)
          : null,
        orderField: `${moment(dataPoint.date_time).format(precision)}-${
          environmentalDataSourcesById[dataPoint.environmental_data_source_id].human_readable_name
        }`,
      }))
      .reduce(environmentalDrawerDataReducer(environmentalDataSourcesById, analytesById), {})
  );

  drawerData.sort((a: any, b: any) => (a.orderField > b.orderField ? -1 : 1));

  return {
    granularity: response.granularity,
    data: drawerData.map((timeEntry: any) => ({
      title: timeEntry.environmentalDataSource.human_readable_name,
      domain: timeEntry.environmentalDataSource.domain,
      date: timeEntry.displayDate,
      date_format: displayPrecision[response.granularity as keyof typeof displayPrecision],
      source: 'environmental_data_source',
      source_data: timeEntry,
    })),
  };
}

/**
 * Extracts unique group tags from an array of EnvironmentalDataSource objects
 * @param dataSources Array of EnvironmentalDataSource objects
 * @returns Array of unique group tags
 */
export function extractUniqueGroupTags(dataSources: EnvironmentalDataSource[]): string[] {
  const allGroupTags: string[] = [];
  dataSources
    .filter((source) => source.map_groupings && Array.isArray(source.map_groupings))
    .forEach((source) => {
      source.map_groupings.forEach((tag) => allGroupTags.push(tag));
    });

  return [...new Set(allGroupTags)].sort();
}
